import { Box } from '@mui/material'
import { get, keys } from 'lodash-es'
import { ModifierOptionContract, ModifierOptionType } from 'models'
import { useMemo } from 'react'
import { UsedModifier } from 'store/models/sellModels'
import { getModifierOptionPrice } from 'utilities/price/getPrice'
import {
    getModOptionName,
    NONE_OPTION,
} from '../../../../components/Sell/utils/common'
import { useCartContext } from './CartContext'
import { DisplayLine } from './CartItemDetail'
import { sumModifierQuantity } from 'views/Sale/utils/orderItemUtil'

interface CartModifierRowProps {
    lineId: string
    usedModifiers: UsedModifier[]
    menuId: string | undefined
    bindingId: string
    itemQuantity?: number
    isOrderLinedVoided?: boolean
}

export const CartModifierRow = ({
    lineId,
    bindingId,
    usedModifiers,
    menuId,
    itemQuantity = 1,
    isOrderLinedVoided = false,
}: CartModifierRowProps) => {
    const {
        modifierList,
        modifierOptionsMap,
        priceListData,
        removedData,
        unavailableProducts,
    } = useCartContext()

    const modOptions = useMemo(() => {
        const modWithQuantity = sumModifierQuantity(usedModifiers)
        return modWithQuantity.map((usedMod) => {
            const option: ModifierOptionContract | undefined =
                modifierOptionsMap[usedMod.modifierOptionId] ??
                get(removedData.modifierOptionsMap, usedMod.modifierOptionId)
            const entityModifier =
                modifierList.find((a) => a.entityModifierId === bindingId) ??
                (removedData?.modifierList ?? []).find(
                    (a) => a.entityModifierId === bindingId,
                )
            const price = entityModifier?.hasAssociatedCost
                ? getModifierOptionPrice(
                      option?.modifierOptionID ?? '',
                      menuId,
                      priceListData,
                  ) *
                  usedMod.quantity *
                  itemQuantity
                : undefined
            let isActive = true

            const subOptionBindings = keys(usedMod.subModifierOptions)
            if (
                !(
                    option?.locationModifierOptions?.[0]
                        ?.isAvailableForSelection ?? false
                ) ||
                unavailableProducts.unavailableSalesItems?.some(
                    (i) => i.itemID === (option?.itemID ?? ''),
                ) ||
                unavailableProducts.unavailableStockItems?.some(
                    (i) => i.itemID === (option?.itemID ?? ''),
                )
            ) {
                isActive = false
            }

            return {
                id: option?.modifierOptionID ?? '',
                name: getModOptionName(option),
                quantity: usedMod.quantity,
                isDeduct: option?.type === ModifierOptionType.Deduct,
                price,
                isActive,
                subOptionBindings,
                usedMod,
            }
        })
    }, [
        usedModifiers,
        modifierOptionsMap,
        removedData.modifierOptionsMap,
        removedData?.modifierList,
        modifierList,
        menuId,
        priceListData,
        itemQuantity,
        unavailableProducts.unavailableSalesItems,
        unavailableProducts.unavailableStockItems,
        bindingId,
    ])
    return (
        <>
            {modOptions.map((op, idx) => {
                return (
                    <Box key={`${idx}-${op?.id}-cart-item-display`}>
                        <DisplayLine
                            isDeduct={op.isDeduct}
                            name={op.name}
                            price={op.price}
                            quantity={op.quantity}
                            isActive={op.isActive}
                            hidePrice={isOrderLinedVoided}
                        />
                        <Box pl={4}>
                            {op.subOptionBindings.flatMap((subBindingId) => {
                                return (
                                    <CartModifierRow
                                        key={`${lineId}-${subBindingId}-${bindingId}-option`}
                                        lineId={lineId}
                                        bindingId={subBindingId}
                                        usedModifiers={op.usedMod.subModifierOptions[
                                            subBindingId
                                        ]
                                            .filter(
                                                (a) =>
                                                    a.modifierOptionId !==
                                                    NONE_OPTION,
                                            )
                                            .map<UsedModifier>((option) => {
                                                return {
                                                    modifierOptionId:
                                                        option.modifierOptionId,
                                                    addPrice: option.addPrice,
                                                    subModifierOptions: {},
                                                }
                                            })}
                                        menuId={menuId}
                                        itemQuantity={
                                            itemQuantity * op.quantity
                                        }
                                        isOrderLinedVoided={isOrderLinedVoided}
                                    />
                                )
                            })}
                        </Box>
                    </Box>
                )
            })}
        </>
    )
}
